// api/nosisApi.js
// Funciones para interactuar con la API de Nosis

import axios from "axios"

// ============================================
// API de consulta a Nosis
// ============================================

const API_BASE = window.REACT_APP_URL || ""
const BASE_URL = `${API_BASE}/ewanapi/v1/titular`

/**
 * Consulta Nosis para un documento/CUIT
 * @param {string} documento - CUIT o documento a consultar
 * @returns {Promise<Object>} Respuesta de Nosis procesada
 */
export const consultarNosis = async documento => {
  try {
    const response = await axios.get(`${BASE_URL}/nosis2/${documento}`)
    return procesarRespuestaNosis(response.data)
  } catch (error) {
    console.error("Error consultando Nosis:", error)
    throw error
  }
}

/**
 * Consulta Nosis para múltiples documentos
 * @param {string[]} documentos - Array de documentos a consultar
 * @returns {Promise<Object[]>} Array de respuestas procesadas
 */
export const consultarNosisMultiple = async documentos => {
  const resultados = await Promise.allSettled(
    documentos.map(doc => consultarNosis(doc)),
  )

  return resultados.map((resultado, index) => ({
    documento: documentos[index],
    exito: resultado.status === "fulfilled",
    datos: resultado.status === "fulfilled" ? resultado.value : null,
    error: resultado.status === "rejected" ? resultado.reason?.message : null,
  }))
}

/**
 * Procesa la respuesta de Nosis y extrae información relevante
 * @param {Object} respuesta - Respuesta cruda del backend
 * @returns {Object} Datos procesados
 */
export const procesarRespuestaNosis = respuesta => {
  if (!respuesta) {
    return { exito: false, error: "Sin respuesta de Nosis" }
  }

  // La respuesta viene directamente con "variables" como array
  const variables = respuesta.variables || []

  if (variables.length === 0) {
    return { exito: false, error: "Sin variables en la respuesta" }
  }

  // Extraer variables clave
  const variablesMap = {}
  variables.forEach(v => {
    // Usar "Nombre" o "nombre" según venga
    const nombre = v.Nombre || v.nombre
    const valor = v.Valor || v.valor
    if (nombre) {
      variablesMap[nombre] = {
        valor: valor,
        descripcion: v.Descripcion || v.descripcion,
        tipo: v.Tipo || v.tipo,
        fechaActualizacion: v.FechaAct || v.fechaAct,
        detalle: v.Detalle || v.detalle,
      }
    }
  })

  // Analizar riesgo basado en variables clave
  const analisis = analizarRiesgoNosis(variablesMap)

  return {
    exito: true,
    cuit: respuesta.cuit,
    razonSocial: extraerValor(variablesMap, "VI_RazonSocial"),
    fechaConsulta: respuesta.fechaConsulta,
    variables: variablesMap,
    variablesArray: variables,
    analisis,
    // Campos principales para mostrar
    score: extraerValorNumerico(variablesMap, "SCO_Vig"),
    dictamen: extraerValor(variablesMap, "CDA_DICT"),
    criterioAceptacion: extraerValor(variablesMap, "CDA"),
    situacionBCRA: extraerValor(variablesMap, "CI_Vig_PeorSit"),
    cantidadBancos: extraerValorNumerico(variablesMap, "CI_Vig_Total_CantBcos"),
    montoTotal: extraerValorNumerico(variablesMap, "CI_Vig_Total_Monto"),
    esMoroso: extraerValor(variablesMap, "DX_Es") === "Si",
    tieneFacturasApocrifas: extraerValor(variablesMap, "FA_Tiene") === "Si",
    tieneDeudasFiscales: extraerValor(variablesMap, "DF_Tiene") === "Si",
    cantidadJuicios: extraerValorNumerico(variablesMap, "JU_12m_RZ_Cant"),
    cantidadConcursos: extraerValorNumerico(variablesMap, "CQ_12m_RZ_Cant"),
    perfilCumplimiento: extraerValorNumerico(
      variablesMap,
      "VR_12m_Cumplimiento",
    ),
    antiguedadBCRA: extraerValorNumerico(variablesMap, "CI_Antiguedad"),
    cantidadEmpleados: extraerValorNumerico(variablesMap, "VI_Empleados_Cant"),
    edadEmpresa: extraerValorNumerico(variablesMap, "VI_Edad_Empresas"),
    tipoSocietario: extraerValor(variablesMap, "VI_TipoSocietario"),
    actividadPrincipal: extraerValor(variablesMap, "VI_Act01_Descrip"),
    sectorActividad: extraerValor(variablesMap, "VI_Act01_Sector"),
    // Compliance
    esSujetoObligado: extraerValor(variablesMap, "COM_SO_Es") === "Si",
    cantidadHomonimosLAFT: extraerValorNumerico(
      variablesMap,
      "COM_Homonimos_LAFT_Cant",
    ),
    linkHomonimosLAFT: extraerValor(variablesMap, "COM_Homonimos_LAFT_Link"),
  }
}

/**
 * Extrae el valor de una variable del mapa
 */
const extraerValor = (variablesMap, nombre) => {
  return variablesMap[nombre]?.valor || ""
}

/**
 * Extrae el valor numérico de una variable
 */
const extraerValorNumerico = (variablesMap, nombre) => {
  const valor = variablesMap[nombre]?.valor
  if (!valor || valor === "" || valor === "-") return null
  const num = parseInt(valor, 10)
  return isNaN(num) ? null : num
}

/**
 * Analiza el riesgo basado en las variables de Nosis
 * @param {Object} variablesMap - Mapa de variables
 * @returns {Object} Análisis de riesgo
 */
const analizarRiesgoNosis = variablesMap => {
  const alertas = []
  let nivelRiesgo = "BAJO"
  let puntajeRiesgo = 0

  // ============================================
  // Score crediticio
  // ============================================
  const score = extraerValorNumerico(variablesMap, "SCO_Vig")
  if (score !== null) {
    if (score < 300) {
      alertas.push({
        tipo: "CRITICO",
        categoria: "SCORE",
        mensaje: `Score muy bajo: ${score}`,
        variable: "SCO_Vig",
        valor: score,
      })
      puntajeRiesgo += 50
    } else if (score < 500) {
      alertas.push({
        tipo: "ALTO",
        categoria: "SCORE",
        mensaje: `Score bajo: ${score}`,
        variable: "SCO_Vig",
        valor: score,
      })
      puntajeRiesgo += 30
    } else if (score < 600) {
      alertas.push({
        tipo: "MEDIO",
        categoria: "SCORE",
        mensaje: `Score moderado: ${score}`,
        variable: "SCO_Vig",
        valor: score,
      })
      puntajeRiesgo += 10
    }
  }

  // ============================================
  // Situación BCRA
  // ============================================
  const situacionBCRA = extraerValorNumerico(variablesMap, "CI_Vig_PeorSit")
  if (situacionBCRA !== null && situacionBCRA >= 2) {
    if (situacionBCRA >= 4) {
      alertas.push({
        tipo: "CRITICO",
        categoria: "BCRA",
        mensaje: `Situación BCRA crítica: ${situacionBCRA}`,
        variable: "CI_Vig_PeorSit",
        valor: situacionBCRA,
      })
      puntajeRiesgo += 50
    } else if (situacionBCRA >= 3) {
      alertas.push({
        tipo: "ALTO",
        categoria: "BCRA",
        mensaje: `Situación BCRA irregular: ${situacionBCRA}`,
        variable: "CI_Vig_PeorSit",
        valor: situacionBCRA,
      })
      puntajeRiesgo += 30
    } else {
      alertas.push({
        tipo: "MEDIO",
        categoria: "BCRA",
        mensaje: `Situación BCRA con atraso: ${situacionBCRA}`,
        variable: "CI_Vig_PeorSit",
        valor: situacionBCRA,
      })
      puntajeRiesgo += 15
    }
  }

  // ============================================
  // Peor situación últimos 12 meses
  // ============================================
  const peorSit12m = extraerValorNumerico(variablesMap, "CI_12m_PeorSit")
  if (peorSit12m !== null && peorSit12m >= 3) {
    alertas.push({
      tipo: peorSit12m >= 4 ? "CRITICO" : "ALTO",
      categoria: "BCRA_HISTORICO",
      mensaje: `Peor situación últimos 12 meses: ${peorSit12m}`,
      variable: "CI_12m_PeorSit",
      valor: peorSit12m,
    })
    puntajeRiesgo += peorSit12m >= 4 ? 40 : 20
  }

  // ============================================
  // Morosidad
  // ============================================
  const esMoroso = extraerValor(variablesMap, "DX_Es")
  if (esMoroso === "Si") {
    alertas.push({
      tipo: "CRITICO",
      categoria: "MOROSIDAD",
      mensaje: "Registra morosidad",
      variable: "DX_Es",
      valor: "Si",
    })
    puntajeRiesgo += 40
  }

  // ============================================
  // Juicios
  // ============================================
  const juicios = extraerValorNumerico(variablesMap, "JU_12m_RZ_Cant")
  if (juicios !== null && juicios > 0) {
    alertas.push({
      tipo: juicios > 2 ? "CRITICO" : "ALTO",
      categoria: "JUICIOS",
      mensaje: `Juicios últimos 12 meses: ${juicios}`,
      variable: "JU_12m_RZ_Cant",
      valor: juicios,
    })
    puntajeRiesgo += juicios > 2 ? 40 : 25
  }

  // ============================================
  // Concursos y quiebras
  // ============================================
  const concursos = extraerValorNumerico(variablesMap, "CQ_12m_RZ_Cant")
  if (concursos !== null && concursos > 0) {
    alertas.push({
      tipo: "CRITICO",
      categoria: "CONCURSOS",
      mensaje: `Concursos/Quiebras últimos 12 meses: ${concursos}`,
      variable: "CQ_12m_RZ_Cant",
      valor: concursos,
    })
    puntajeRiesgo += 50
  }

  // ============================================
  // Pedidos de quiebra
  // ============================================
  const pedidosQuiebra = extraerValorNumerico(variablesMap, "PQ_12m_Cant")
  if (pedidosQuiebra !== null && pedidosQuiebra > 0) {
    alertas.push({
      tipo: "CRITICO",
      categoria: "PEDIDO_QUIEBRA",
      mensaje: `Pedidos de quiebra últimos 12 meses: ${pedidosQuiebra}`,
      variable: "PQ_12m_Cant",
      valor: pedidosQuiebra,
    })
    puntajeRiesgo += 50
  }

  // ============================================
  // Cheques rechazados
  // ============================================
  const chequesRechazados = extraerValorNumerico(
    variablesMap,
    "HC_6m_SF_NoPag_Cant",
  )
  if (chequesRechazados !== null && chequesRechazados > 0) {
    alertas.push({
      tipo: chequesRechazados > 3 ? "CRITICO" : "ALTO",
      categoria: "CHEQUES",
      mensaje: `Cheques rechazados sin pagar (6m): ${chequesRechazados}`,
      variable: "HC_6m_SF_NoPag_Cant",
      valor: chequesRechazados,
    })
    puntajeRiesgo += chequesRechazados > 3 ? 35 : 20
  }

  // ============================================
  // Facturas apócrifas
  // ============================================
  const facturasApocrifas = extraerValor(variablesMap, "FA_Tiene")
  if (facturasApocrifas === "Si") {
    alertas.push({
      tipo: "CRITICO",
      categoria: "FACTURAS_APOCRIFAS",
      mensaje: "Registra facturas apócrifas",
      variable: "FA_Tiene",
      valor: "Si",
    })
    puntajeRiesgo += 50
  }

  // ============================================
  // Deudas fiscales
  // ============================================
  const deudasFiscales = extraerValor(variablesMap, "DF_Tiene")
  if (deudasFiscales === "Si") {
    alertas.push({
      tipo: "ALTO",
      categoria: "DEUDAS_FISCALES",
      mensaje: "Registra deudas fiscales",
      variable: "DF_Tiene",
      valor: "Si",
    })
    puntajeRiesgo += 25
  }

  // ============================================
  // Homónimos LA/FT (Lavado de Activos / Financiamiento del Terrorismo)
  // ============================================
  const homonimosLAFT = extraerValorNumerico(
    variablesMap,
    "COM_Homonimos_LAFT_Cant",
  )
  if (homonimosLAFT !== null && homonimosLAFT > 0) {
    alertas.push({
      tipo: "MEDIO",
      categoria: "COMPLIANCE",
      mensaje: `Homónimos en base LA/FT: ${homonimosLAFT}`,
      variable: "COM_Homonimos_LAFT_Cant",
      valor: homonimosLAFT,
      link: extraerValor(variablesMap, "COM_Homonimos_LAFT_Link"),
    })
    puntajeRiesgo += 10
  }

  // ============================================
  // Dictamen Nosis
  // ============================================
  const dictamen = extraerValor(variablesMap, "CDA_DICT")
  if (dictamen && dictamen !== "APROBADO") {
    alertas.push({
      tipo: dictamen === "RECHAZADO" ? "CRITICO" : "MEDIO",
      categoria: "DICTAMEN",
      mensaje: `Dictamen Nosis: ${dictamen}`,
      variable: "CDA_DICT",
      valor: dictamen,
    })
    if (dictamen === "RECHAZADO") puntajeRiesgo += 30
  }

  // ============================================
  // Determinar nivel de riesgo final
  // ============================================
  if (puntajeRiesgo >= 80 || alertas.some(a => a.tipo === "CRITICO")) {
    nivelRiesgo = "CRITICO"
  } else if (
    puntajeRiesgo >= 50 ||
    alertas.filter(a => a.tipo === "ALTO").length >= 2
  ) {
    nivelRiesgo = "ALTO"
  } else if (puntajeRiesgo >= 20 || alertas.some(a => a.tipo === "ALTO")) {
    nivelRiesgo = "MEDIO"
  }

  return {
    nivelRiesgo,
    puntajeRiesgo,
    alertas,
    cantidadAlertas: alertas.length,
    alertasCriticas: alertas.filter(a => a.tipo === "CRITICO").length,
    alertasAltas: alertas.filter(a => a.tipo === "ALTO").length,
    alertasMedias: alertas.filter(a => a.tipo === "MEDIO").length,
  }
}

/**
 * Organiza las variables por categoría para mostrar en UI
 * @param {Array} variables - Array de variables de Nosis
 * @returns {Object} Variables organizadas por categoría
 */
export const organizarVariablesPorCategoria = variables => {
  const categorias = {
    identificacion: {
      titulo: "Identificación",
      icon: "bx-id-card",
      variables: [],
    },
    domicilio: {
      titulo: "Domicilio",
      icon: "bx-map",
      variables: [],
    },
    actividad: {
      titulo: "Actividad Económica",
      icon: "bx-briefcase",
      variables: [],
    },
    situacionFiscal: {
      titulo: "Situación Fiscal",
      icon: "bx-receipt",
      variables: [],
    },
    creditoBancario: {
      titulo: "Crédito Bancario (BCRA)",
      icon: "bx-bank",
      variables: [],
    },
    antecedentes: {
      titulo: "Antecedentes Negativos",
      icon: "bx-error-circle",
      variables: [],
    },
    scoring: {
      titulo: "Scoring y Evaluación",
      icon: "bx-bar-chart-alt-2",
      variables: [],
    },
    compliance: {
      titulo: "Compliance",
      icon: "bx-shield",
      variables: [],
    },
    relaciones: {
      titulo: "Relaciones Societarias",
      icon: "bx-group",
      variables: [],
    },
    otros: {
      titulo: "Otros",
      icon: "bx-dots-horizontal-rounded",
      variables: [],
    },
  }

  const prefijoCategoriaMap = {
    VI_: "identificacion",
    VI_Dom: "domicilio",
    VI_Act: "actividad",
    VI_Inscrip: "situacionFiscal",
    VI_Emplead: "actividad",
    CI_: "creditoBancario",
    DX_: "antecedentes",
    HC_: "antecedentes",
    JU_: "antecedentes",
    CQ_: "antecedentes",
    PQ_: "antecedentes",
    FA_: "antecedentes",
    DF_: "antecedentes",
    RC_: "antecedentes",
    SCO_: "scoring",
    CDA: "scoring",
    VR_: "scoring",
    NSE: "scoring",
    FE: "scoring",
    COM_: "compliance",
    VR_Relaciones: "relaciones",
    MC_: "relaciones",
  }

  variables.forEach(variable => {
    const nombre = variable.Nombre || variable.nombre
    const valor = variable.Valor || variable.valor

    // Ignorar valores vacíos o solo para individuos si es empresa
    if (!valor || valor === "" || valor === "-") return

    let categoriaAsignada = "otros"

    // Buscar categoría por prefijo
    for (const [prefijo, categoria] of Object.entries(prefijoCategoriaMap)) {
      if (nombre.startsWith(prefijo)) {
        categoriaAsignada = categoria
        break
      }
    }

    categorias[categoriaAsignada].variables.push({
      nombre,
      valor,
      descripcion: variable.Descripcion || variable.descripcion,
      tipo: variable.Tipo || variable.tipo,
      fechaActualizacion: variable.FechaAct || variable.fechaAct,
    })
  })

  // Filtrar categorías vacías
  return Object.fromEntries(
    Object.entries(categorias).filter(([, cat]) => cat.variables.length > 0),
  )
}

/**
 * Variables clave de Nosis para mostrar en resumen
 */
export const VARIABLES_NOSIS_CLAVE = [
  { nombre: "SCO_Vig", etiqueta: "Score", esNumero: true },
  { nombre: "CDA_DICT", etiqueta: "Dictamen" },
  { nombre: "CI_Vig_PeorSit", etiqueta: "Situación BCRA" },
  {
    nombre: "CI_Vig_Total_CantBcos",
    etiqueta: "Cantidad Bancos",
    esNumero: true,
  },
  {
    nombre: "CI_Vig_Total_Monto",
    etiqueta: "Monto Total Deuda",
    esMoneda: true,
  },
  {
    nombre: "CI_Antiguedad",
    etiqueta: "Antigüedad BCRA (meses)",
    esNumero: true,
  },
  { nombre: "VR_12m_Cumplimiento", etiqueta: "Perfil Cumplimiento" },
  { nombre: "DX_Es", etiqueta: "Es Moroso", esBooleano: true },
  { nombre: "JU_12m_RZ_Cant", etiqueta: "Juicios (12m)", esNumero: true },
  {
    nombre: "CQ_12m_RZ_Cant",
    etiqueta: "Concursos/Quiebras (12m)",
    esNumero: true,
  },
  {
    nombre: "HC_6m_SF_NoPag_Cant",
    etiqueta: "Cheques Rechazados (6m)",
    esNumero: true,
  },
  { nombre: "FA_Tiene", etiqueta: "Facturas Apócrifas", esBooleano: true },
  { nombre: "DF_Tiene", etiqueta: "Deudas Fiscales", esBooleano: true },
]

export const analizarRespuestaNosis = procesarRespuestaNosis

export default {
  consultarNosis,
  consultarNosisMultiple,
  procesarRespuestaNosis,
  analizarRespuestaNosis,
  organizarVariablesPorCategoria,
  VARIABLES_NOSIS_CLAVE,
}
